/**
 * GuideEngine state + directive interpreter (Agentic UI Fabric P0).
 *
 * receive() is called from the SSE dispatch path (non-React context).
 * Semantics per the design doc:
 *   - navigate: registry lookup; unknown target NO-OPs + emits `dismissed`
 *     (steering feedback), never throws. A hit runs the entry's activation
 *     and reports `surface_changed`.
 *   - spotlight: sets desired state; FabricGuideHost renders it REACTIVELY
 *     the moment a target registers (registry subscription — event-driven,
 *     no timers, which is how a spotlight following a navigate survives the
 *     target surface still mounting). User dismissal emits `dismissed`.
 *   - prompt/notify: folded UIAction verbs — chat input store / toast.
 * Any other verb, render included, is a no-op: no prompt emits render, and a
 * block appended to the client cache alone would vanish on the next read.
 */
import { create } from 'zustand'

import type { DirectiveWire } from '@/api/types'
import { toast } from '@/hooks/use-toast'
// The host→agent surface-event channel died with the conversation cut-over:
// it rode POST /sessions/{id}/ui_actions, a route that does not exist
// upstream. The agent→host half (turn.directives on the session's own event
// stream) is handled here unchanged; steering feedback waits for
// tricorder-session's fabric design (its SPEC's UI directive work).
import { useChatInputStore } from '@/stores/chatInput'
import { useFabricRegistryStore } from '@/stores/fabricRegistry'
import { createLogger } from '@/utils/logger'

const logger = createLogger('fabricGuide')

interface FabricGuideState {
  active: { sessionId: string; guideIds: string[]; coachMark: string } | null
  receive: (sessionId: string, directive: DirectiveWire) => void
  dismiss: () => void
}

export const useFabricGuideStore = create<FabricGuideState>((set, get) => ({
  active: null,
  receive: (sessionId, d) => {
    const registry = useFabricRegistryStore.getState()
    if (d.navigate) {
      // Unicast, not broadcast: a navigate is the agent replying to the tab
      // that is IN this conversation, but the SSE channel is per-user — every
      // open tab receives the frame. Executing it in each one launched the
      // same agent once per tab (two orphan sessions, both tabs hijacked —
      // mvp, 2026-07-17). Only a tab with a mounted surface for this session
      // (engaged via usePendingTurnPoll) executes; the rest drop SILENTLY —
      // a `dismissed` here would have N-1 idle tabs reporting false failure
      // over the owner tab's real outcome. The same gate covers prompt and
      // notify below (prompt writes the single app-wide composer store — in a
      // non-engaged tab that clobbers text the nurse is mid-typing in a
      // DIFFERENT conversation; a notify for a conversation this tab isn't in
      // is noise). Spotlight stays ungated: visual and idempotent, and
      // a spotlight must be able to land right after its preceding navigate
      // swapped the pane away (the swap may disengage the session before a
      // late spotlight frame arrives).
      if (!registry.isSessionEngaged(sessionId)) {
        logger.debug('navigate_dropped_not_engaged', { sessionId })
        return
      }
      const target = d.navigate.targetGuideId ?? ''
      const entry = registry.get(target)
      // Co-pilot ceiling (structural): the agent may activate ONLY a
      // `navigate` entry. A control/commit/field/widget is the nurse's own
      // tap and must never be agent-driven — so a missing target, a
      // non-navigate kind, or an entry with no activate() all no-op and emit
      // `dismissed` (steering feedback), exactly like the unknown-target path.
      if (!entry || entry.kind !== 'navigate' || !entry.activate) {
        logger.debug('navigate_feedback_dropped_no_channel', { sessionId, guideId: target })
        return
      }
      entry.activate()
      return
    }
    if (d.spotlight) {
      set({
        active: {
          sessionId,
          guideIds: d.spotlight.guideIds ?? [],
          coachMark: d.spotlight.coachMark ?? '',
        },
      })
      return
    }
    if (d.prompt) {
      // Same engagement gate as navigate (see comment there): the composer
      // store is app-wide, so a non-engaged tab must not have its input
      // clobbered by another conversation's prompt directive.
      if (!registry.isSessionEngaged(sessionId)) {
        logger.debug('prompt_dropped_not_engaged', { sessionId })
        return
      }
      useChatInputStore.getState().setValue(d.prompt.text ?? '')
      return
    }
    if (d.notify) {
      if (!registry.isSessionEngaged(sessionId)) {
        logger.debug('notify_dropped_not_engaged', { sessionId })
        return
      }
      toast({ description: d.notify.message ?? '' })
      return
    }
    // Unknown / future verb (P1 stage, guide, compose): no-op by design.
    logger.debug('directive_verb_unhandled', { sessionId })
  },
  dismiss: () => {
    const a = get().active
    if (a) {
      logger.debug('dismiss_feedback_dropped_no_channel', { sessionId: a.sessionId })
    }
    set({ active: null })
  },
}))
